import { createFileRoute } from "@tanstack/react-router"
import { Page } from "../../../../../-components/Page"
import { useSuspenseQueryGet } from "../../../../../typed-client/hooks/use-suspense-query-get"
import { ActionIcon, Group, Stack, Text } from "@mantine/core"
import { useRouter } from "@tanstack/react-router"
import {
  useContractsGetAvailableWorkflowsSuspense,
  useContractsGetContractTrackingWorkflowStepsSuspense,
} from "@/orval/gen"
import { useDisclosure } from "@mantine/hooks"
import { ApproveContractModal } from "../-components/ApproveContractModal"
import { ContractDatesCard } from "@/components/contracts/ContractDatesCard"
import { ContractFieldsCard } from "@/components/contracts/ContractFieldsCard"
import { ContractEngagementsCard } from "@/components/contracts/ContractEngagementsCard"
import { ContractDetailsCard } from "@/components/contracts/ContractDetailsCard"
import { ColumnLayout } from "../../../-components/column-layout"
import { ContractTrackingTimeline } from "../-components/ContractTrackingTimeline"
import { IconHistory, IconPencilCheck } from "@tabler/icons-react"
import { ContractTrackingModal } from "../-components/ContractTrackingModal"
import { ConractHoverCard } from "../-components/ConractHoverCard"
import { ContractConfirmationsCard } from "@/components/contracts/ContractConfirmationsCard"
import { useTranslation } from "react-i18next"
import { DetailsCard } from "@/routes/app/-components/details-card"
import { PersonName } from "@components/PersonName"

export const Route = createFileRoute("/app/contracts/approvals/$contractId/")({
  component: RouteComponent,
  beforeLoad: () => ({ getTitle: () => "" }),
})

function RouteComponent() {
  const contractId = Route.useParams({
    select: (params) => parseInt(params.contractId),
  })
  return <ContractDetailsPage contractId={contractId} />
}

export function ContractDetailsPage({ contractId }: { contractId: number }) {
  const { t } = useTranslation("app_server")
  const { data: contract } = useSuspenseQueryGet(
    "/api/Contracts/GetContractDetails",
    { id: contractId }
  )

  const productionActivities = contract.engagements.flatMap((engagement) => ({
    productionTitle: engagement.production,
    contractDates: engagement.contractDates,
  }))

  const router = useRouter()

  React.useEffect(() => {
    router.invalidate()
  }, [contract])

  const [opened, { open, close }] = useDisclosure()

  const { data: approvalStatuses } = useContractsGetAvailableWorkflowsSuspense({
    contractId,
  })

  const [historyOpened, { open: openHistory, close: closeHistory }] =
    useDisclosure()

  const { data: workflows } =
    useContractsGetContractTrackingWorkflowStepsSuspense({ contractId })

  const workflowTrackedStatuses = workflows.filter((w) => w.tracking !== null)

  const showHistory =
    contract.contractTrackings.flatMap((v) => v.trackings).length >
    workflowTrackedStatuses.length

  const trackingStartIndex = contract.contractTrackings
    .flatMap((v) => v.trackings)
    .findLastIndex(
      (v) =>
        v.contractTrackingStatus.contractApprovalStatusId ===
        workflows.at(0)?.contractApprovalStatus.contractApprovalStatusId
    )

  const documentAvailable = contract.confirmations.some((c) =>
    c.contractDocumentAttachments.some((a) => a.extension === ".pdf")
  )

  return (
    <Page
      crumbOverride={() => (
        <Text truncate fz="xl" fw={"bold"}>
          {contract.contractTypeName}{" "}
          <PersonName personId={contract.personId} />
        </Text>
      )}
      buttonGroupProps={{
        children: approvalStatuses.length > 0 && (
          <ActionIcon onClick={open}>
            <IconPencilCheck />
          </ActionIcon>
        ),
      }}
    >
      <Group w={"100%"} align="top">
        <ColumnLayout flex={1}>
          <ContractDetailsCard contract={contract} />
          <ApproveContractModal
            documentAvailable={documentAvailable}
            contractId={contract.contractId}
            opened={opened}
            onClose={close}
          />
          <ContractEngagementsCard engagements={contract.engagements} />
          <ContractDatesCard data={productionActivities} />
          <ContractFieldsCard fieldsGrouped={contract.contractFields} />
          <ContractConfirmationsCard confirmations={contract.confirmations} />
        </ColumnLayout>
        <ContractTrackingModal
          trackingStartIndex={trackingStartIndex}
          opened={historyOpened}
          onClose={closeHistory}
          title="History"
          trackings={contract.contractTrackings}
        />
        <DetailsCard
          stackProps={{ w: "fit-content" }}
          title={t("ContractTracking")}
        >
          <Stack w={"fit-content"}>
            <Group style={{ alignSelf: "end" }}>
              {contract.confirmations.length > 0 && (
                <ConractHoverCard confirmations={contract.confirmations} />
              )}
              {showHistory && (
                <ActionIcon onClick={openHistory}>
                  <IconHistory />
                </ActionIcon>
              )}
            </Group>
            <ContractTrackingTimeline
              contractId={contractId}
              workflows={workflows}
              personId={contract.personId}
              documentAvailable={documentAvailable}
            />
          </Stack>
        </DetailsCard>
      </Group>
    </Page>
  )
}
